import { useState } from "react";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download } from "lucide-react";
import { downloadExport } from "./shared/downloadExport";
import { PaginationBar } from "@/components/shared/PaginationBar";

const fmt = (n) => (n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });

function defaultFrom() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function BulletsByType({ site }) {
  const [dateFrom, setDateFrom] = useState(defaultFrom());
  const [dateTo, setDateTo] = useState(today());
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = (pageNum = 1) => {
    if (!site) return toast.error("Select a site first");
    setLoading(true);
    client
      .get("/bullets-register/by-type", { params: { site, date_from: dateFrom, date_to: dateTo, page: pageNum, page_size: 50 } })
      .then((res) => {
        setRows(res.data.items);
        setTotalPages(res.data.total_pages);
        setPage(res.data.page);
      })
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  };

  const exportXlsx = async () => {
    if (!site) return toast.error("Select a site first");
    try {
      await downloadExport("/bullets-register/export/by-type", { site, date_from: dateFrom, date_to: dateTo });
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="bullets-by-type">
      <div className="flex flex-wrap items-center gap-3">
        <Input type="date" data-testid="bullets-by-type-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-44" />
        <span className="text-sm text-slate-400">to</span>
        <Input type="date" data-testid="bullets-by-type-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-44" />
        <Button onClick={() => load(1)} disabled={loading || !site} data-testid="bullets-by-type-load" className="bg-amber-600 hover:bg-amber-700">
          {loading ? "Loading..." : "Load"}
        </Button>
        <Button variant="outline" onClick={exportXlsx} disabled={!site} data-testid="bullets-by-type-export">
          <Download className="h-4 w-4 mr-1.5" /> Export Excel
        </Button>
      </div>

      {rows && (
        <>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <Table data-testid="bullets-by-type-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Bullet Type</TableHead>
                  <TableHead>Total Received</TableHead>
                  <TableHead>Total Replaced</TableHead>
                  <TableHead>Replacement Events</TableHead>
                  <TableHead>Closing Balance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.bullet_type} data-testid={`bullets-by-type-row-${r.bullet_type}`}>
                    <TableCell className="font-medium">{r.bullet_type}</TableCell>
                    <TableCell className="font-mono">{fmt(r.total_received)}</TableCell>
                    <TableCell className="font-mono">{fmt(r.total_replaced)}</TableCell>
                    <TableCell>{r.replacement_events_count}</TableCell>
                    <TableCell className={`font-mono font-semibold ${r.closing_balance < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmt(r.closing_balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <PaginationBar page={page} totalPages={totalPages} onPageChange={load} testId="bullets-by-type-pagination" />
        </>
      )}
    </div>
  );
}
